import React from 'react';
import dayjs from 'dayjs';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ReservationStatisticsInnerResponseOutDTO, ReservationStatisticsResponseOutDTO } from '@api/ringil-api.v2';
import { CalendarViewType, daysInView } from '../calendarView';

const StatsBox = ({ title, bgcolor, data }: { title: string; bgcolor: string; data: ReservationStatisticsInnerResponseOutDTO }) => {
  return (
    <div className="flex p-4 bg-white rounded shadow-[0px_16px_48px_0px_rgba(254,80,0,0.02),0px_2px_8px_0px_rgba(0,0,0,0.05)] border-solid border border-zinc-300 flex-col">
      <strong className="text-zinc-800 text-md mb-4 font-normal">{title}</strong>

      <div className="flex flex-col gap-y-2 w-full text-sm text-content-secondary">
        {data.days.map(item => (
          <div className="relative flex flex-row w-full" key={item.date}>
            <div className="w-1/5 shrink-0 whitespace-nowrap">{dayjs(item.date).locale('cs').format('D. MMMM')}</div>
            <div className="grow font-bold text-right pr-4">{item.count}</div>
            <div className="w-3/5 shrink-0 h-4 bg-gray-200 rounded-sm">
              {!!data.sum && !!item.count && (
                <div
                  className={clsx(
                    'rounded-sm h-full transition-all w-0',
                    bgcolor === 'green' ? 'bg-complementary-success' : bgcolor === 'blue' ? 'bg-complementary-processing' : 'bg-complementary-warning',
                  )}
                  style={{ width: `${Math.ceil(100 / (data.sum / item.count))}%` }}
                ></div>
              )}
            </div>
          </div>
        ))}

        <div className="flex relative w-full flex-row">
          <div className="w-1/5 shrink-0">Celkem</div>
          <div className="grow font-bold text-right pr-4">{data.sum}</div>
          <div className="w-3/5 shrink-0 h-4 bg-gray-200 rounded-sm">
            <div
              className={clsx(
                'rounded-sm h-full transition-all w-full',
                bgcolor === 'green' ? 'bg-complementary-success' : bgcolor === 'blue' ? 'bg-complementary-processing' : 'bg-complementary-warning',
              )}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface Props {
  stats: ReservationStatisticsResponseOutDTO;
  statsActive: boolean[];
  currentView: CalendarViewType;
}

/** The three aggregated-statistics boxes shown above the grid. */
export const CalendarStats: React.FC<Props> = ({ stats, statsActive, currentView }) => {
  const { tr } = useTranslationLgs();
  const days = daysInView(currentView);

  return (
    <>
      <div className="text-zinc-600 inline-flex gap-x-4 items-center cursor-pointer text-lg mb-2">
        <span>
          Plán na {days} {days === 1 ? 'den' : 'dní'}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-8 mb-8">
        {stats.countReservationStats && statsActive[0] && (
          <StatsBox data={stats.countReservationStats} title={tr('TimeslotReservations.ReservationsAmountBox', 'Reservations Amount')} bgcolor="green" />
        )}
        {stats.countManUnitsStats && statsActive[1] && (
          <StatsBox data={stats.countManUnitsStats} title={tr('TimeslotReservations.PlannedManipulationUnitsBox', 'Planned Manipulation Units')} bgcolor="blue" />
        )}
        {stats.countTimeStats && statsActive[2] && (
          <StatsBox data={stats.countTimeStats} title={tr('TimeslotReservations.TotalHoursReservationCountBox', 'Total Hours Reservation Count')} bgcolor="yellow" />
        )}
      </div>
    </>
  );
};